import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button } from '../../Components/Button/Button'
import uuidv4 from '../../utils/uuidv4'

/**
 * Bronson Toggle object.
 * The `toggle` pattern is an accessible content-reveal object. It can show/hide (or expand/collapse) a {@link targetRef} element.
 * It can be used standalone or placed within other components to reveal/hide a target element.
 * Each Toggle object has a 1-to-1 relationship to its target, but a target can be controlled via multiple `toggle` patterns.
 * @see https://bronson.vwfs.tools/default/docs/objects/toggle.html
 *
 * @param {string} [className] - Additional CSS classes on the object element.
 * @param {boolean} [defaultHidden=true] - Set the initial hidden state, defaults to `true`.
 * @param {string} [id] - The `[id]` attribute of the main toggle element.
 * @param {string} [labelHidden] - The label to display in collapsed state.
 * @param {string} [labelUnhidden] - The label to display in expanded state.
 * @param {function} [onClick] - Optional `onClick` handler.
 * @param {string} [targetId] - The toggle target id.
 * @param {React.Ref} [targetRef] - The toggle target reference.
 * @param {string} [testId] - The `[data-testid]` attribute on the object element.
 * @param {object} [otherProps] - Other props passed to the object element.
 * @returns {JSX.Element} - The Toggle object.
 * @constructor
 */
export function Toggle({
  className,
  defaultHidden = true,
  id,
  labelHidden,
  labelUnhidden,
  onClick,
  targetId,
  targetRef,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('o-toggle', 'js-toggle', className).trim()
  const [hidden, setHidden] = useState(defaultHidden)
  const toggleId = id ?? uuidv4()

  /**
   * Handle side effects for the {@link targetRef}.
   */
  useEffect(() => {
    if (targetRef?.current) {
      targetRef?.current.setAttribute('aria-hidden', hidden)
      targetRef?.current.setAttribute('aria-labelledby', toggleId)
    }
    /**
     * Cleanup function.
     */
    return () => {}
  }, [hidden, targetRef, toggleId])

  /**
   * Handle click events on the {@link Toggle} button.
   * @type {(function(*): void)|*}
   */
  const handleClick = useCallback(
    (event) => {
      event.preventDefault()
      event.stopPropagation()
      setHidden(!hidden)
      /**
       * Call custom callback.
       */
      onClick && onClick()
    },
    [hidden, onClick]
  )

  return (
    <Button
      className={classNameList}
      id={toggleId}
      onClick={handleClick}
      ariaExpanded={!hidden}
      aria-controls={targetId}
      testId={testId}
      {...otherProps}
    >
      {/* Pass {@link Toggle} object labels as children. */}
      <span className="o-toggle__label o-toggle__label--target-hidden">{labelHidden}</span>
      <span className="o-toggle__label o-toggle__label--target-unhidden">{labelUnhidden}</span>
    </Button>
  )
}

Toggle.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the object element.
  defaultHidden: PropTypes.bool,
  id: PropTypes.string,
  labelHidden: PropTypes.string,
  labelUnhidden: PropTypes.string,
  onClick: PropTypes.func,
  targetId: PropTypes.string,
  targetRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
